import { Box, keyframes } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import logoFull from "../../../logo-full.svg";
import {
	CoAppLoginCard,
	CoAppLoginContainer,
	CoAppLoginFooter
} from "../../global/styled/global.styled";
import { SCREEN } from "../login-utils/flowInteractions";

const STEPS_WITH_LOGO = new Set<string>([SCREEN.ENTER_EMAIL, SCREEN.ORG_PICKER]);

const stepIn = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: translateY(0);   }
`;

export type LoginLayoutProps = {
	/** Descope BYOS screen name — drives logo visibility and step animation key. */
	screenName: string;
	children: ReactNode;
	/**
	 * When true (default), only render logo + animated step content.
	 * SignIn / Extension already provide CoAppLoginContainer, card, and footer.
	 */
	embedded?: boolean;
	/** Include footer inside layout — use for standalone pages only. */
	showFooter?: boolean;
};

function LoginStepChrome({ screenName, children }: Pick<LoginLayoutProps, "screenName" | "children">) {
	const showLogo = STEPS_WITH_LOGO.has(screenName);

	const [logoMounted, setLogoMounted] = useState(showLogo);
	useEffect(() => {
		if (showLogo) setLogoMounted(true);
	}, [showLogo]);

	return (
		<>
			{logoMounted && (
				<Box
					component="img"
					src={logoFull}
					alt="CoApp"
					onTransitionEnd={() => {
						if (!showLogo) setLogoMounted(false);
					}}
					sx={{
						width: 168,
						height: "auto",
						margin: "0 auto 4px",
						display: "block",
						pointerEvents: "none",
						userSelect: "none",
						opacity: showLogo ? 1 : 0,
						transform: showLogo ? "translateY(0)" : "translateY(-4px)",
						transition: "opacity 0.2s ease, transform 0.2s ease"
					}}
				/>
			)}

			<Box
				key={screenName}
				sx={{
					animation: `${stepIn} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) both`,
					display: "flex",
					flexDirection: "column",
					gap: 0
				}}
			>
				{children}
			</Box>
		</>
	);
}

export default function LoginLayout({
	screenName,
	children,
	embedded = true,
	showFooter = false
}: LoginLayoutProps) {
	const step = <LoginStepChrome screenName={screenName}>{children}</LoginStepChrome>;

	if (embedded) {
		return <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>{step}</Box>;
	}

	return (
		<CoAppLoginContainer>
			<CoAppLoginCard
				sx={{
					transition: "min-height 0.28s cubic-bezier(0.4, 0, 0.2, 1)"
				}}
			>
				{step}
				{showFooter && (
					<CoAppLoginFooter>
						<a href="https://coapp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
						<span aria-hidden="true">·</span>
						<a href="https://coapp.io/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
						<span aria-hidden="true">·</span>
						<a href="mailto:support@coapp.io">Support</a>
					</CoAppLoginFooter>
				)}
			</CoAppLoginCard>
		</CoAppLoginContainer>
	);
}
